/**
 * Encryption Service - AES-256 File Encryption/Decryption
 *
 * Provides secure encryption for sensitive evidence files.
 * Users can optionally encrypt files before upload for maximum confidentiality.
 *
 * Security Features:
 * - New files: chunked AES-256-GCM, PBKDF2-SHA256 (600k iterations), see cryptoV2.js
 * - Legacy files (CryptoJS passphrase mode, "U2FsdGVk..." text) remain decryptable
 * - Client-side encryption (key never leaves browser)
 * - Password strength validation
 * - Support for all file types
 */

import CryptoJS from 'crypto-js';
import { encryptBlob, decryptBytes, isV2 } from './cryptoV2';

class EncryptionService {
  constructor() {
    this.minKeyLength = 6;
    this.recommendedKeyLength = 16;
    console.log('[EncryptionService] Initialized with min key length:', this.minKeyLength);
  }

  /**
   * Validates encryption key strength
   * @param {string} key - The encryption key to validate
   * @returns {Object} - Validation result with strength score
   */
  validateKey(key) {
    console.log('[EncryptionService] Validating key - Length:', key ? key.length : 0);

    if (!key || key.length < this.minKeyLength) {
      console.log('[EncryptionService] Key validation FAILED - Too short');
      return {
        isValid: false,
        error: `The key must be at least ${this.minKeyLength} characters long`,
        strength: 0,
        strengthLabel: 'Too Weak'
      };
    }

    // Calculate key strength
    let score = 0;

    // Length scoring
    if (key.length >= 6) score += 1;
    if (key.length >= 12) score += 1;
    if (key.length >= 16) score += 1;

    // Complexity scoring
    if (/[A-Z]/.test(key)) score += 1; // Has uppercase
    if (/[a-z]/.test(key)) score += 1; // Has lowercase
    if (/[0-9]/.test(key)) score += 1; // Has number
    if (/[^A-Za-z0-9]/.test(key)) score += 1; // Has special char

    // Determine strength label
    let strengthLabel, color;
    if (score <= 2) {
      strengthLabel = 'Weak';
      color = '#f44336'; // red
    } else if (score <= 4) {
      strengthLabel = 'Medium';
      color = '#ff9800'; // orange
    } else if (score <= 6) {
      strengthLabel = 'Strong';
      color = '#4caf50'; // green
    } else {
      strengthLabel = 'Very Strong';
      color = '#2e7d32'; // dark green
    }

    console.log('[EncryptionService] Key validation PASSED - Strength:', strengthLabel, '(Score:', score, ')');

    return {
      isValid: true,
      error: '',
      strength: score,
      strengthLabel,
      color
    };
  }

  /**
   * Encrypts a file using AES-256 encryption
   * @param {File} file - The file to encrypt
   * @param {string} key - The encryption key
   * @returns {Promise<Object>} - Encrypted data and metadata
   */
  async encryptFile(file, key) {
    try {
      console.log('[EncryptionService] Starting file encryption:', file.name);

      // Validate the key
      const keyValidation = this.validateKey(key);
      if (!keyValidation.isValid) {
        return { success: false, error: keyValidation.error };
      }

      // Chunked AES-256-GCM container (see cryptoV2.js); binary, no base64 inflation
      const encryptedBlob = await encryptBlob(file, key);

      console.log('[EncryptionService] File encrypted successfully (EVC2)');
      console.log('[EncryptionService] Original size:', file.size, 'bytes');
      console.log('[EncryptionService] Encrypted size:', encryptedBlob.size, 'bytes');

      const encryptedFile = new File(
        [encryptedBlob],
        `${file.name}.encrypted`,
        { type: 'application/octet-stream' }
      );

      return {
        success: true,
        encryptedFile: encryptedFile,
        originalFileName: file.name,
        originalFileType: file.type,
        originalFileSize: file.size,
        encryptedSize: encryptedFile.size
      };

    } catch (error) {
      console.error('[EncryptionService] Encryption error:', error);
      return {
        success: false,
        error: error.message || 'An error occurred during encryption'
      };
    }
  }

  /**
   * Decrypts encrypted file data (EVC2 container or legacy CryptoJS text)
   * @param {string|ArrayBuffer|Uint8Array} encryptedData - Legacy string, or raw bytes of either format
   * @param {string} key - The decryption key
   * @param {string} originalFileType - Original file MIME type
   * @returns {Promise<Object>} - Decrypted data as Blob and URL
   */
  async decryptFile(encryptedData, key, originalFileType = 'application/octet-stream') {
    try {
      console.log('[EncryptionService] Starting file decryption...');

      // Validate the key
      const keyValidation = this.validateKey(key);
      if (!keyValidation.isValid) {
        return { success: false, error: keyValidation.error };
      }

      if (!encryptedData) {
        return { success: false, error: 'No encrypted data provided' };
      }

      let bytes;
      if (typeof encryptedData !== 'string' && isV2(encryptedData)) {
        // Current format: authenticated, fails loudly on wrong key or tampering
        bytes = await decryptBytes(encryptedData, key);
      } else {
        // Legacy CryptoJS format (base64 text starting "U2FsdGVk...")
        const legacyText = typeof encryptedData === 'string'
          ? encryptedData
          : new TextDecoder().decode(encryptedData);
        const decrypted = CryptoJS.AES.decrypt(legacyText, key);

        // Convert to Latin1 string (binary data)
        const decryptedBytes = decrypted.toString(CryptoJS.enc.Latin1);

        if (!decryptedBytes || decryptedBytes.length === 0) {
          return {
            success: false,
            error: 'Decryption failed. Please check your encryption key.'
          };
        }

        bytes = new Uint8Array(decryptedBytes.length);
        for (let i = 0; i < decryptedBytes.length; i++) {
          bytes[i] = decryptedBytes.charCodeAt(i);
        }
      }

      // Create a Blob from decrypted data
      const blob = new Blob([bytes], { type: originalFileType });
      const url = URL.createObjectURL(blob);

      console.log('[EncryptionService] File decrypted successfully');
      console.log('[EncryptionService] Decrypted size:', bytes.length, 'bytes');

      return {
        success: true,
        blob: blob,
        url: url,
        size: bytes.length,
        type: originalFileType
      };

    } catch (error) {
      console.error('[EncryptionService] Decryption error:', error);
      return {
        success: false,
        error: 'Decryption failed. Please verify your key and encrypted data.'
      };
    }
  }

  /**
   * Downloads a blob as a file
   * @param {Blob} blob - The blob to download
   * @param {string} filename - The filename to save as
   */
  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Prints a blob (if it's an image or PDF)
   * @param {Blob} blob - The blob to print
   */
  printBlob(blob) {
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    } else {
      alert('Please allow popups to print the file');
    }
  }

  /**
   * Generates a timestamp-based filename
   * @param {string} prefix - Prefix for the filename
   * @param {string} extension - File extension
   * @returns {string} - Generated filename
   */
  generateFilename(prefix, extension) {
    const now = new Date();
    const formattedDate = now
      .toISOString()
      .replace(/[-:]/g, '')
      .replace('T', '_')
      .split('.')[0];

    return `${prefix}_${formattedDate}.${extension.toLowerCase()}`;
  }

  /**
   * Gets file extension from filename or MIME type
   * @param {string} filename - Original filename
   * @param {string} mimeType - MIME type
   * @returns {string} - File extension
   */
  getFileExtension(filename, mimeType) {
    // Try to get from filename first
    if (filename && filename.includes('.')) {
      const parts = filename.split('.');
      return parts[parts.length - 1];
    }

    // Fallback to MIME type mapping
    const mimeMap = {
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/gif': 'gif',
      'application/pdf': 'pdf',
      'text/plain': 'txt',
      'text/html': 'html',
      'application/json': 'json',
      'video/mp4': 'mp4',
      'audio/mpeg': 'mp3',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
    };

    return mimeMap[mimeType] || 'bin';
  }

  /**
   * Determines if a file type can be previewed in browser
   * @param {string} mimeType - MIME type
   * @returns {boolean} - True if can preview
   */
  canPreview(mimeType) {
    const previewable = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/svg+xml',
      'application/pdf',
      'text/plain',
      'text/html',
      'application/json',
      'video/mp4',
      'video/webm',
      'audio/mpeg',
      'audio/wav'
    ];

    return previewable.includes(mimeType);
  }
}

// Export singleton instance
const encryptionService = new EncryptionService();

export default encryptionService;
export { EncryptionService };
